// BaseReadService.js

/**
 * BaseReadService.js
 * 
 * Service for reading base data from Firestore.
 */

// Firebase
import { collectionGroup, getDocs, onSnapshot, query, where } from 'firebase/firestore';
import { db } from 'firebaseConfig';
import {
  getBaseBillingRecord,
  getBaseLockReason,
} from 'services/billing/BaseBillingService';
import { getPlanByKey } from 'services/plans/PlanService';
import {
  getBaseDocSnap,
  ownerBaseQueries,
  subscribeToBaseDoc,
  uniqueBaseKeysFromMemberDocs,
} from 'utilities/baseDualRead';

/**
 * Live base-document listeners held by the roster subscription.
 *
 * Membership is unbounded — a user can belong to hundreds of bases — so the roster
 * hydrates every membership once and keeps only this many under a live listener. The
 * base the user is actually in stays live through its own dedicated subscription
 * (`subscribeToBase`), which the roster merges over its rows.
 */
export const MAX_LIVE_MEMBER_BASES = 8;

export function getBaseKey(base) {
  const key = typeof base?.key === 'string' ? base.key.trim() : '';
  if (key) return key;

  const id = typeof base?.id === 'string' ? base.id.trim() : '';
  return id || '';
}

const toBaseRow = (docSnap) => {
  const data = docSnap.data() || {};
  const normalizedKey = String(data?.key || docSnap.id || '').trim() || docSnap.id;
  const visibility = String(data?.visibility || '').trim().toLowerCase() === 'public'
    ? 'public'
    : 'private';

  return {
    id: docSnap.id,
    ...data,
    key: normalizedKey,
    visibility,
  };
};

function formatFsError(error) {
  return {
    code: error?.code || null,
    message: error?.message || String(error || ''),
    name: error?.name || null,
  };
}

function toMillisOrMax(value) {
  if (!value) return Number.POSITIVE_INFINITY;

  if (typeof value?.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : Number.POSITIVE_INFINITY;
  }

  if (typeof value?.seconds === 'number') {
    const nanos = typeof value?.nanoseconds === 'number' ? value.nanoseconds : 0;
    return (value.seconds * 1000) + Math.floor(nanos / 1e6);
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : Number.POSITIVE_INFINITY;
  }

  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
  }

  return Number.POSITIVE_INFINITY;
}

function pickFallbackBase(bases, userKey) {
  const list = Array.isArray(bases) ? bases.filter(Boolean) : [];
  if (!list.length) return null;

  return [...list].sort((a, b) => {
    const aIsDefault = a?.isDefault === true ? 0 : 1;
    const bIsDefault = b?.isDefault === true ? 0 : 1;
    if (aIsDefault !== bIsDefault) return aIsDefault - bIsDefault;

    const aOwned = a?.ownerKey === userKey ? 0 : 1;
    const bOwned = b?.ownerKey === userKey ? 0 : 1;
    if (aOwned !== bOwned) return aOwned - bOwned;

    const aCreated = toMillisOrMax(a?.dateCreated);
    const bCreated = toMillisOrMax(b?.dateCreated);
    if (aCreated !== bCreated) return aCreated - bCreated;

    const aTitle = String(a?.title || '').trim();
    const bTitle = String(b?.title || '').trim();
    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);

    const aKey = getBaseKey(a) || '';
    const bKey = getBaseKey(b) || '';
    return aKey.localeCompare(bKey);
  })[0] || null;
}

async function loadOwnedBaseRows(userKey) {
  const { basesQuery } = ownerBaseQueries(userKey);
  const basesSnap = await getDocs(basesQuery);

  const baseMap = new Map();
  basesSnap.docs.forEach((docSnap) => {
    const row = toBaseRow(docSnap);
    const key = getBaseKey(row);
    if (key) baseMap.set(key, row);
  });
  return baseMap;
}

async function loadAllBasesForUser(userKey) {
  const cleanedUserKey = String(userKey || '').trim();
  if (!cleanedUserKey) {
    return [];
  }

  const baseMap = await loadOwnedBaseRows(cleanedUserKey);

  const membershipSnapshot = await getDocs(query(
    collectionGroup(db, 'members'),
    where('userKey', '==', cleanedUserKey)
  ));

  const membershipBaseKeys = uniqueBaseKeysFromMemberDocs(membershipSnapshot.docs);

  await Promise.all(membershipBaseKeys.map(async (baseKey) => {
    if (baseMap.has(baseKey)) return;

    try {
      const base = await getBase(baseKey);
      const key = getBaseKey(base);
      if (key) baseMap.set(key, base);
    } catch (error) {
      console.warn('[BaseReadService] loadAllBasesForUser membership base failed:', {
        userKey: cleanedUserKey,
        baseKey,
        error: formatFsError(error),
      });
    }
  }));

  return Array.from(baseMap.values());
}

async function isBaseAccessible(base, now = new Date()) {
  if (!base) return false;

  const baseKey = getBaseKey(base);
  if (!baseKey) return false;

  let billingRecord = null;
  let plan = null;

  try {
    billingRecord = await getBaseBillingRecord(baseKey);
  } catch (error) {
    console.warn('[BaseReadService] isBaseAccessible billing lookup failed:', {
      baseKey,
      error: formatFsError(error),
    });
  }

  const planKey = String(base?.planKey || base?.planId || '').trim();
  if (planKey && base?.planBypassesTrialLock !== true) {
    try {
      plan = await getPlanByKey({ planKey });
    } catch (error) {
      console.warn('[BaseReadService] isBaseAccessible plan lookup failed:', {
        planKey,
        error: formatFsError(error),
      });
    }
  }

  return !getBaseLockReason(base, billingRecord, now, plan);
}

async function pickAccessibleBase(bases, userKey, preferredBaseKey = '') {
  const list = Array.isArray(bases) ? bases.filter(Boolean) : [];
  if (!list.length) return null;

  const cleanedPreferredBaseKey = String(preferredBaseKey || '').trim();
  if (cleanedPreferredBaseKey) {
    const preferredBase = list.find((base) => getBaseKey(base) === cleanedPreferredBaseKey);
    if (preferredBase && await isBaseAccessible(preferredBase)) {
      return preferredBase;
    }
  }

  const sortedBases = [...list].sort((a, b) => {
    const aIsDefault = a?.isDefault === true ? 0 : 1;
    const bIsDefault = b?.isDefault === true ? 0 : 1;
    if (aIsDefault !== bIsDefault) return aIsDefault - bIsDefault;

    const aOwned = a?.ownerKey === userKey ? 0 : 1;
    const bOwned = b?.ownerKey === userKey ? 0 : 1;
    if (aOwned !== bOwned) return aOwned - bOwned;

    const aCreated = toMillisOrMax(a?.dateCreated);
    const bCreated = toMillisOrMax(b?.dateCreated);
    if (aCreated !== bCreated) return aCreated - bCreated;

    const aTitle = String(a?.title || '').trim();
    const bTitle = String(b?.title || '').trim();
    if (aTitle !== bTitle) return aTitle.localeCompare(bTitle);

    const aKey = getBaseKey(a) || '';
    const bKey = getBaseKey(b) || '';
    return aKey.localeCompare(bKey);
  });

  for (const base of sortedBases) {
    if (await isBaseAccessible(base)) {
      return base;
    }
  }

  return pickFallbackBase(list, userKey);
}

export async function getBase(baseKey) {
  const cleanedBaseKey = String(baseKey || '').trim();
  if (!cleanedBaseKey) return null;

  const resolved = await getBaseDocSnap(cleanedBaseKey);
  return resolved?.snap ? toBaseRow(resolved.snap) : null;
}

export async function getInitialBaseForUser({ userKey, preferredBaseKey }) {
  const cleanedUserKey = String(userKey || '').trim();
  const cleanedPreferredBaseKey = String(preferredBaseKey || '').trim();

  if (!cleanedUserKey) {
    return null;
  }

  try {
    const bases = await loadAllBasesForUser(cleanedUserKey);
    if (bases.length) {
      return pickAccessibleBase(bases, cleanedUserKey, cleanedPreferredBaseKey);
    }
  } catch (error) {
    console.warn('[BaseReadService] getInitialBaseForUser accessible base lookup failed:', {
      userKey: cleanedUserKey,
      preferredBaseKey: cleanedPreferredBaseKey,
      error: formatFsError(error),
    });
  }

  if (cleanedPreferredBaseKey) {
    try {
      const preferredBase = await getBase(cleanedPreferredBaseKey);
      if (preferredBase) {
        return preferredBase;
      }
    } catch (error) {
      console.warn('[BaseReadService] getInitialBaseForUser preferred base failed:', {
        userKey: cleanedUserKey,
        baseKey: cleanedPreferredBaseKey,
        error: formatFsError(error),
      });
    }
  }

  const ownedMap = await loadOwnedBaseRows(cleanedUserKey);
  const ownedBase = pickFallbackBase(Array.from(ownedMap.values()), cleanedUserKey);
  if (ownedBase) {
    return ownedBase;
  }

  const membershipSnapshot = await getDocs(query(
    collectionGroup(db, 'members'),
    where('userKey', '==', cleanedUserKey)
  ));

  const membershipBaseKeys = uniqueBaseKeysFromMemberDocs(membershipSnapshot.docs)
    .sort((left, right) => left.localeCompare(right));

  if (!membershipBaseKeys.length) {
    return null;
  }

  return getBase(membershipBaseKeys[0]);
}

/**
 * Subscribes to bases for a specific user.
 *
 * @param {string} userKey - The user key to scope bases.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToBases(userKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Require auth context from caller; without a userKey we return no bases.
  if (!userKey) {
    callback([]);
    return () => { };
  }

  const baseMap = new Map();
  const sourceByBase = new Map();

  // Every membership base is hydrated once; only a bounded subset stays live.
  const hydratedMemberKeys = new Set();
  const liveMemberUnsubs = new Map();

  const emitRows = () => {
    callback(Array.from(baseMap.values()));
  };

  const removeSourceForBase = (baseKey, source) => {
    const sources = sourceByBase.get(baseKey);
    if (!sources) return;

    sources.delete(source);
    if (sources.size === 0) {
      sourceByBase.delete(baseKey);
      baseMap.delete(baseKey);
    }

    emitRows();
  };

  const setBaseForSource = (baseKey, source, row) => {
    if (!sourceByBase.has(baseKey)) {
      sourceByBase.set(baseKey, new Set());
    }

    sourceByBase.get(baseKey).add(source);
    baseMap.set(baseKey, row);
    emitRows();
  };

  const applySnapshot = (source, snapshot) => {
    const nextKeys = new Set();

    snapshot.docs.forEach((docSnap) => {
      const key = docSnap.id;
      nextKeys.add(key);

      if (!sourceByBase.has(key)) {
        sourceByBase.set(key, new Set());
      }
      sourceByBase.get(key).add(source);
      baseMap.set(key, toBaseRow(docSnap));
    });

    for (const [key, sources] of sourceByBase.entries()) {
      if (!sources.has(source)) continue;
      if (nextKeys.has(key)) continue;

      sources.delete(source);
      if (sources.size === 0) {
        sourceByBase.delete(key);
        baseMap.delete(key);
      }
    }

    emitRows();
  };

  const bootstrapSourceFor = (baseKey) => `member-bootstrap:${baseKey}`;
  const liveSourceFor = (baseKey) => `member-live:${baseKey}`;

  const hydrateMemberBase = (baseKey) => {
    if (!baseKey || hydratedMemberKeys.has(baseKey)) return;

    hydratedMemberKeys.add(baseKey);

    const bootstrapSource = bootstrapSourceFor(baseKey);

    getBaseDocSnap(baseKey)
      .then((resolved) => {
        if (!resolved?.snap) {
          removeSourceForBase(baseKey, bootstrapSource);
          return;
        }

        setBaseForSource(baseKey, bootstrapSource, toBaseRow(resolved.snap));
      })
      .catch((error) => {
        console.error('[BaseReadService] member-base:bootstrap-error', {
          baseKey,
          error: formatFsError(error),
        });
        removeSourceForBase(baseKey, bootstrapSource);
      });
  };

  const attachLiveMemberBase = (baseKey) => {
    if (!baseKey || liveMemberUnsubs.has(baseKey)) return;

    const liveSource = liveSourceFor(baseKey);

    const unsubscribe = subscribeToBaseDoc(
      baseKey,
      (docSnap) => {
        if (!docSnap) {
          removeSourceForBase(baseKey, liveSource);
          return;
        }

        setBaseForSource(baseKey, liveSource, toBaseRow(docSnap));
      },
      (error) => {
        console.error('[BaseReadService] member-base:live-error', {
          baseKey,
          error: formatFsError(error),
        });
        removeSourceForBase(baseKey, liveSource);
      },
    );

    liveMemberUnsubs.set(baseKey, unsubscribe);
  };

  const detachLiveMemberBase = (baseKey) => {
    const unsubscribe = liveMemberUnsubs.get(baseKey);
    if (!unsubscribe) return;

    unsubscribe();
    liveMemberUnsubs.delete(baseKey);

    // The hydrated row stays; only the live channel goes away.
    removeSourceForBase(baseKey, liveSourceFor(baseKey));
  };

  const forgetMemberBase = (baseKey) => {
    detachLiveMemberBase(baseKey);
    hydratedMemberKeys.delete(baseKey);
    removeSourceForBase(baseKey, bootstrapSourceFor(baseKey));
  };

  const { basesQuery } = ownerBaseQueries(userKey);

  const membershipsQuery = query(
    collectionGroup(db, 'members'),
    where('userKey', '==', userKey)
  );

  const unsubOwnerBases = onSnapshot(
    basesQuery,
    (snapshot) => {
      applySnapshot('ownerKey:bases', snapshot);
    },
    () => {
      emitRows();
    },
  );

  const unsubMemberships = onSnapshot(
    membershipsQuery,
    (snapshot) => {
      const nextBaseKeys = new Set(uniqueBaseKeysFromMemberDocs(snapshot.docs));

      nextBaseKeys.forEach((baseKey) => hydrateMemberBase(baseKey));

      // Sorted so the live set is stable across snapshots instead of shuffling with
      // Firestore's document order.
      const liveBaseKeys = new Set(
        Array.from(nextBaseKeys)
          .sort((left, right) => left.localeCompare(right))
          .slice(0, MAX_LIVE_MEMBER_BASES)
      );

      liveBaseKeys.forEach((baseKey) => attachLiveMemberBase(baseKey));

      Array.from(liveMemberUnsubs.keys())
        .filter((baseKey) => !liveBaseKeys.has(baseKey))
        .forEach((baseKey) => detachLiveMemberBase(baseKey));

      Array.from(hydratedMemberKeys)
        .filter((baseKey) => !nextBaseKeys.has(baseKey))
        .forEach((baseKey) => forgetMemberBase(baseKey));
    },
    (error) => {
      console.error('[BaseReadService] memberships:query-error', {
        userKey,
        error: formatFsError(error),
      });
    }
  );

  return () => {
    unsubOwnerBases();
    unsubMemberships();
    Array.from(hydratedMemberKeys).forEach((baseKey) => forgetMemberBase(baseKey));
  };
}

/**
 * Subscribes to a single base by key.
 *
 * @param {string} baseKey - Base document id.
 * @param {function} callback - Callback function to handle real-time updates.
 *
 * @returns {function(): void} Unsubscribe function
 */
export function subscribeToBase(baseKey, callback) {

  // Validate callback
  if (typeof callback !== 'function') {
    return () => { };
  }

  // Validate baseKey
  if (!baseKey) {
    callback(null);
    return () => { };
  }

  return subscribeToBaseDoc(
    baseKey,
    (docSnap) => {
      callback(docSnap ? toBaseRow(docSnap) : null);
    },
    (error) => {
      console.error('[BaseReadService] subscribeToBase error:', error);
      callback(null);
    },
  );
}

export { isBaseAccessible, loadAllBasesForUser };
